import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import axios from 'axios';

interface User {
  id: string;
  email: string;
  name: string;
  role?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserFromToken = useCallback(async (authToken: string): Promise<User | null> => {
    try {
      const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_URL || '/api/auth';
      const response = await axios.get(`${AUTH_SERVICE_URL}/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data?.success && response.data?.user) {
        return response.data.user;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('auth_user');

      if (storedToken) {
        if (storedUser) {
          try {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
          } catch {
            localStorage.removeItem('auth_user');
          }
        }
        const userData = await fetchUserFromToken(storedToken);
        if (userData) {
          setToken(storedToken);
          setUser(userData);
          localStorage.setItem('auth_user', JSON.stringify(userData));
        } else {
          setToken(null);
          setUser(null);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
        }
      } else if (import.meta.env.DEV) {
        const demoUser = {
          id: 'demo-user-1',
          email: 'admin@molochain.com',
          name: 'Admin User',
          role: 'admin'
        };
        const demoToken = 'demo-token-otms-2025';
        setToken(demoToken);
        setUser(demoUser);
        localStorage.setItem('auth_token', demoToken);
        localStorage.setItem('auth_user', JSON.stringify(demoUser));
      }
      setIsLoading(false);
    };
    initAuth();
  }, [fetchUserFromToken]);

  const login = async (email: string, password: string) => {
    try {
      const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_URL || '/api/auth';
      const response = await axios.post(`${AUTH_SERVICE_URL}/login`, {
        email,
        password,
      });

      const { token: authToken, user: userData } = response.data;
      
      setToken(authToken);
      setUser(userData);
      localStorage.setItem('auth_token', authToken);
      localStorage.setItem('auth_user', JSON.stringify(userData));
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        isAuthenticated: !!token,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
